import { CircleNotchIcon } from "@phosphor-icons/react";
import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from "react";

import { cn } from "../cn.js";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: ButtonVariant;
	size?: ButtonSize;
	/**
	 * Shows a spinner and blocks further clicks. Every asynchronous action in
	 * the product is required to surface a pending state (spec §6.3), and
	 * routing it through this prop also prevents double submission.
	 */
	pending?: boolean;
	/** Announced while `pending` is true. */
	pendingLabel?: string;
	iconLeft?: ReactNode;
	iconRight?: ReactNode;
	fullWidth?: boolean;
}

const VARIANTS: Record<ButtonVariant, string> = {
	primary:
		"bg-(--color-brand-600) text-white hover:bg-(--color-brand-700) active:bg-(--color-brand-800) disabled:bg-(--color-brand-300)",
	secondary:
		"bg-(--surface) text-(--text-primary) border border-(--border-strong) hover:bg-(--surface-sunken) active:bg-(--color-ink-100)",
	ghost: "bg-transparent text-(--text-secondary) hover:bg-(--surface-sunken) hover:text-(--text-primary)",
	danger:
		"bg-(--color-critical-600) text-white hover:bg-(--color-critical-700) active:bg-(--color-critical-700) disabled:bg-(--color-critical-500)",
};

const SIZES: Record<ButtonSize, string> = {
	// Touch targets stay at or above 40px so they remain usable on a phone.
	sm: "h-9 px-3 text-sm gap-1.5",
	md: "h-10 px-4 text-sm gap-2",
	lg: "h-11 px-5 text-base gap-2",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
	{
		variant = "secondary",
		size = "md",
		pending = false,
		pendingLabel,
		iconLeft,
		iconRight,
		fullWidth = false,
		className,
		children,
		disabled,
		type = "button",
		...props
	},
	ref,
) {
	return (
		<button
			{...props}
			ref={ref}
			type={type}
			disabled={disabled || pending}
			aria-busy={pending || undefined}
			className={cn(
				"inline-flex items-center justify-center rounded-(--radius-md) font-medium",
				"transition-colors duration-150",
				"disabled:cursor-not-allowed disabled:opacity-70",
				VARIANTS[variant],
				SIZES[size],
				fullWidth && "w-full",
				className,
			)}
		>
			{pending ? (
				<CircleNotchIcon className="size-4 animate-spin" weight="bold" aria-hidden="true" />
			) : (
				iconLeft
			)}
			<span>{pending && pendingLabel ? pendingLabel : children}</span>
			{!pending && iconRight}
		</button>
	);
});
